import React, { useEffect } from "react";
import { viewPDF } from "../../utils/viewPDF";
import { Box, Button } from "@mui/material";
import { CURRENCY_INR } from "../../utils/constant";

const ViewAllBidsTableExport = ({
  rfq,
  bidData,
  groupedData,
  groupedWorkItems,
  storedUser,
}) => {
  const columnVisibility = {};

  let globalShowRate = false;
  let globalShowRemark = false;

  Object.values(groupedData).forEach((bids) => {
    bids.forEach((bid) => {
      const hasNonINR = bid.bidWorkItems.some(
        (item) => item.currency?.currency?.shortform !== CURRENCY_INR
      );

      const hasRemark = bid.bidWorkItems.some(
        (item) => item.remark && item.remark.trim() !== ""
      );

      if (hasNonINR) globalShowRate = true;
      if (hasRemark) globalShowRemark = true;
    });
  });

  Object.entries(groupedData).forEach(([vendor, bids]) => {
    bids.forEach((_, index) => {
      columnVisibility[`${vendor}-${index}`] = {
        showRate: globalShowRate,
        showRemark: globalShowRemark,
      };
    });
  });

  useEffect(() => {
    const rightRows = document.querySelectorAll("#rightTable tbody tr");
    rightRows.forEach((row) => (row.style.height = "auto"));

    const leftRows = document.querySelectorAll("#leftTable tbody tr");

    for (let i = 0; i < leftRows.length; i++) {
      const height = Math.max(leftRows[i].getBoundingClientRect().height, 40);
      if (rightRows[i]) {
        rightRows[i].style.height = `${height}px`;
      }
    }
  }, [groupedWorkItems, groupedData]);

  const latestBids =
    rfq && groupedData
      ? Object.entries(groupedData).map(([vendor, bids]) => {
          const mostRecent = [...bids].sort(
            (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
          )[0];
          return { vendor, bid: mostRecent };
        })
      : [];

  const totals = latestBids.map(({ vendor, bid }) => {
    const baseTotal = bid.bidWorkItems.reduce((acc, item) => {
      const rate = item.rate || 0;
      const quantity = item.quantity || 0;
      const conversionRate = item.currency?.rate || 1;

      const subTotal = rate * quantity;
      return (
        acc +
        (item.currency.currency.shortform !== CURRENCY_INR
          ? subTotal * conversionRate
          : subTotal)
      );
    }, 0);

    const calculateExtra = (value, type) => {
      if (!value || !type) return 0;
      if (type === "percentage") return baseTotal * (value / 100);
      return value;
    };

    const packagingExtra = calculateExtra(
      bid.packagingValue,
      bid.packagingType
    );
    const freightExtra = calculateExtra(bid.freightValue, bid.freightType);
    const creditExtra = calculateExtra(bid.creditValue, bid.creditType);

    const finalTotal = baseTotal + packagingExtra + freightExtra + creditExtra;

    return { vendor, total: finalTotal };
  });

  const sortedTotals = [...totals].sort((a, b) => a.total - b.total);
  let lowest = null;
  let secondLowest = null;

  if (sortedTotals.length > 0) {
    lowest = sortedTotals[0];
  }
  if (sortedTotals.length > 1) {
    secondLowest =
      sortedTotals.find((entry) => entry.vendor !== lowest.vendor) || null;
  }

  const getVendorColSpan = (vendor, bids) => {
    return bids.reduce((sum, _, index) => {
      const visibility = columnVisibility[`${vendor}-${index}`];
      let count = 2; // base: rate and currency
      if (visibility?.showRate) count += 1;
      if (visibility?.showRemark) count += 1;
      return sum + count;
    }, 0);
  };

  return (
    <Box sx={{ overflowX: "scroll" }}>
      <table
        id="rightTable"
        style={{
          borderCollapse: "collapse",
          minWidth: `100%`,
          textAlign: "center",
          border: "1px solid #ccc",
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                padding: "9px",
                width: "150px",
                height: "136px",
                border: "1px solid #ccc",
              }}
              rowSpan={4}
            >
              Item Code
            </th>
            <th
              style={{
                padding: "9px",
                width: "240px",
                border: "1px solid #ccc",
              }}
              rowSpan={4}
            >
              Item Description
            </th>
            <th
              style={{
                padding: "9px",
                width: "70px",
                border: "1px solid #ccc",
              }}
              rowSpan={4}
            >
              UOM
            </th>
            <th
              style={{
                padding: "9px",
                width: "70px",
                border: "1px solid #ccc",
              }}
              rowSpan={4}
            >
              Quantity
            </th>
            <th
              style={{
                padding: "9px",
                width: "70px",
                border: "1px solid #ccc",
              }}
              rowSpan={4}
            >
              GST <br /> (in %)
            </th>
            <th
              style={{
                padding: "9px",
                width: "70px",
                borderRight: "1px solid #ccc",
              }}
              rowSpan={4}
            >
              Previous PO Rate (in &#8377;)
            </th>
            <th
              style={{
                padding: "9px",
                width: "70px",
                borderRight: "1px solid #ccc",
              }}
              rowSpan={4}
            >
              SAP PR
            </th>

            {/* Row 1: Vendor Code */}
            {Object.entries(groupedData).map(([vendor, bids], i) => (
              <th
                key={`vendor-name-${vendor}-${i}`}
                colSpan={getVendorColSpan(vendor, bids)}
                style={{
                  padding: "9px",
                  borderBottom: "1px solid #ccc",
                  borderRight: "1px solid #ccc",
                  textAlign: "center",
                }}
              >
                {vendor.split("-")[1]}
              </th>
            ))}
          </tr>

          {/* Row 2: Vendor Name */}
          <tr>
            {/* Vendor Code Row */}
            {Object.entries(groupedData).map(([vendor, bids], i) => (
              <th
                key={`vendor-code-${vendor}`}
                colSpan={getVendorColSpan(vendor, bids)}
                style={{
                  padding: "9px",
                  borderBottom: "1px solid #ccc",
                  borderRight: "1px solid #ccc",
                  textAlign: "center",
                }}
              >
                {vendor.split("-")[0]}
              </th>
            ))}
          </tr>

          <tr>
            {/* Bid Type Row */}
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((_, i) => (
                <th
                  key={`${vendor}-quote-${i}`}
                  colSpan={
                    (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                    2 +
                    (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                  }
                  style={{
                    padding: "8px",
                    borderBottom: "1px solid #ccc",
                    borderRight: "1px solid #ccc",
                    textAlign: "center",
                  }}
                >
                  {i === 0 ? "Initial Quote" : "Final Quote"}
                </th>
              ))
            )}
          </tr>

          {/* Row 4: Rate/Total */}
          <tr>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((_, i) => {
                const { showRate, showRemark } =
                  columnVisibility[`${vendor}-${i}`];
                const columns = [];

                if (showRate) {
                  columns.push(
                    <th
                      key={`${vendor}-rate-${i}`}
                      style={{
                        width: "100px",
                        borderTop: "1px solid #ccc",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      Rate
                    </th>
                  );
                }

                columns.push(
                  <th
                    key={`${vendor}-inr-rate-${i}`}
                    style={{
                      width: "100px",
                      borderTop: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                    }}
                  >
                    Rate (in &#8377;)
                  </th>,
                  <th
                    key={`${vendor}-total-${i}`}
                    style={{
                      width: "100px",
                      borderTop: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                    }}
                  >
                    Total (in &#8377;)
                  </th>
                );

                if (showRemark) {
                  columns.push(
                    <th
                      key={`${vendor}-remark-${i}`}
                      style={{
                        width: "100px",
                        borderTop: "1px solid #ccc",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      Remark
                    </th>
                  );
                }

                return columns;
              })
            )}
          </tr>
        </thead>

        <tbody>
          {Object.entries(groupedWorkItems).map(
            ([section, items], sectionIndex) => (
              <React.Fragment key={sectionIndex}>
                {/* SECTION HEADER */}
                <tr
                  style={{
                    borderTop: "1px solid #ccc",
                    fontWeight: "bold",
                  }}
                >
                  <td
                    colSpan={7}
                    style={{
                      textAlign: "left",
                      padding: "0px 10px",
                      height: "41px",
                    }}
                  >
                    Item Group: {section}
                  </td>
                  {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                    bids.map((bid, i) => {
                      const sectionItems = bid.bidWorkItems.filter((bi) =>
                        items.find((wi) => wi.code === bi.code)
                      );
                      const sectionTotal = sectionItems.reduce((sum, si) => {
                        const rate = si.rate || 0;
                        const quantity = si.quantity || 0;
                        const conversionRate = si.currency?.rate || 1;
                        const gst = si.gst || 0;
                        if (si.currency.currency.shortform !== CURRENCY_INR) {
                          return sum + rate * conversionRate * quantity;
                        } else {
                          return sum + rate * quantity;
                        }
                      }, 0);

                      return (
                        <td
                          key={`${vendor}-sectionTotal-${i}-${sectionIndex}`}
                          colSpan={
                            (columnVisibility[`${vendor}-${i}`]?.showRate
                              ? 1
                              : 0) +
                            2 +
                            (columnVisibility[`${vendor}-${i}`]?.showRemark
                              ? 1
                              : 0)
                          }
                          style={{
                            textAlign: "right",
                            padding: "10px",
                            height: "41px",
                            borderRight: "1px solid #ccc",
                          }}
                        >
                          ₹{" "}
                          {new Intl.NumberFormat("en-IN", {
                            style: "decimal",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(sectionTotal)}
                        </td>
                      );
                    })
                  )}
                </tr>

                {/* ITEM ROWS */}
                {items
                  .sort((a, b) => a.itemOrder - b.itemOrder)
                  .map((item, i) => (
                    <tr key={i}>
                      {/* Base item details */}
                      <td
                        style={{
                          borderTop: "1px solid #ccc",
                          borderRight: "1px solid #ccc",
                          height: "50px",
                        }}
                      >
                        {item.code}
                      </td>
                      <td
                        style={{
                          borderTop: "1px solid #ccc",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        {item.description}
                      </td>
                      <td
                        style={{
                          borderTop: "1px solid #ccc",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        {item.unit}
                      </td>
                      <td
                        style={{
                          borderTop: "1px solid #ccc",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        {item.quantity}
                      </td>
                      <td
                        style={{
                          borderTop: "1px solid #ccc",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        {item.gst}
                      </td>
                      <td
                        style={{
                          borderTop: "1px solid #ccc",
                          borderRight: "1px solid #ccc",
                          padding: "8px",
                        }}
                      >
                        {(item?.poRate ?? 0).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td
                        style={{
                          borderTop: "1px solid #ccc",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        {item.sapPr || ""}
                      </td>

                      {/* Per vendor rates & totals */}
                      {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                        bids.map((bid, i) => {
                          const normalize = (str) =>
                            str?.replace(/\s+/g, " ").trim().toLowerCase();
                          const matchedItem = bid?.bidWorkItems.find(
                            (bi) =>
                              bi.code === item.code &&
                              normalize(bi.description) ===
                                normalize(item.description) &&
                              bi.quantity === item.quantity &&
                              bi.unit === item.unit
                              // bi.gst === item.gst
                          );

                          const rate = matchedItem?.rate || 0;
                          const conversionRate =
                            matchedItem?.currency?.rate || 1;
                          const quantity = matchedItem?.quantity || 0;
                          const gst = matchedItem?.gst || 0;
                          const baseTotal = rate * conversionRate * quantity;
                          const matchedItemTotal =
                            baseTotal + baseTotal * (gst / 100);

                          const { showRate, showRemark } =
                            columnVisibility[`${vendor}-${i}`];

                          return (
                            <React.Fragment key={`${vendor}-item-${i}`}>
                              {showRate && (
                                <td
                                  style={{
                                    borderTop: "1px solid #ccc",
                                    borderRight: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "right",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {matchedItem?.currency?.currency?.symbol}{" "}
                                  {matchedItem?.rate.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }) ?? ""}
                                </td>
                              )}
                              <td
                                style={{
                                  borderTop: "1px solid #ccc",
                                  borderRight: "1px solid #ccc",
                                  padding: "8px",
                                  textAlign: "right",
                                }}
                              >
                                {(rate * conversionRate).toLocaleString(
                                  "en-IN",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}
                              </td>
                              <td
                                style={{
                                  borderTop: "1px solid #ccc",
                                  borderRight: "1px solid #ccc",
                                  padding: "8px",
                                  textAlign: "right",
                                }}
                              >
                                {matchedItem?.currency?.currency?.shortform !==
                                CURRENCY_INR
                                  ? matchedItemTotal.toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                  : (matchedItem?.total || 0).toLocaleString(
                                      "en-IN",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                              </td>
                              {showRemark && (
                                <td
                                  style={{
                                    borderTop: "1px solid #ccc",
                                    borderRight: "1px solid #ccc",
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {matchedItem?.remark || "-"}
                                </td>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tr>
                  ))}
              </React.Fragment>
            )
          )}

          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                borderRight: "1px solid #ccc",
                height: "40px",
                fontWeight: "bold",
              }}
            >
              Total Base Price:
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => {
                const baseTotal = bid.bidWorkItems.reduce((acc, item) => {
                  const rate = item.rate || 0;
                  const quantity = item.quantity || 0;
                  const conversionRate = item.currency?.rate || 1;

                  const baseTotal = rate * quantity;

                  if (item.currency.currency.shortform !== CURRENCY_INR) {
                    return acc + baseTotal * conversionRate;
                  } else {
                    return acc + baseTotal;
                  }
                }, 0);

                return (
                  <td
                    key={bid.bidId}
                    colSpan={
                      (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                      2 +
                      (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                    }
                    style={{
                      textAlign: "right",
                      fontWeight: "bold",
                      paddingInline: "10px",
                      height: "40px",
                      borderRight: "1px solid #ccc",
                      borderTop: "1px solid #ccc",
                    }}
                  >
                    &#8377;{" "}
                    {new Intl.NumberFormat("en-IN", {
                      style: "decimal",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(baseTotal)}
                  </td>
                );
              })
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                borderRight: "1px solid #ccc",
                height: "40px",
                fontWeight: "bold",
              }}
            >
              Packaging:
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => {
                return (
                  <td
                    key={bid.bidId}
                    colSpan={
                      (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                      2 +
                      (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                    }
                    style={{
                      borderTop: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                      paddingInline: "10px",
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    {`${bid?.packagingType === "value" ? `₹` : ""} ${
                      bid?.packagingValue
                    } ${bid?.packagingType === "percentage" ? `%` : ""}`}
                  </td>
                );
              })
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                borderRight: "1px solid #ccc",
                height: "40px",
                fontWeight: "bold",
              }}
            >
              Freight:
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => {
                return (
                  <td
                    key={bid.bidId}
                    colSpan={
                      (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                      2 +
                      (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                    }
                    style={{
                      borderTop: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                      paddingInline: "10px",
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    {`${bid?.freightType === "value" ? `₹` : ""} ${
                      bid?.freightValue
                    } ${bid?.freightType === "percentage" ? `%` : ""}`}
                  </td>
                );
              })
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                borderRight: "1px solid #ccc",
                height: "40px",
                fontWeight: "bold",
              }}
            >
              Credit Cost:
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => {
                return (
                  <td
                    key={bid.bidId}
                    colSpan={
                      (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                      2 +
                      (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                    }
                    style={{
                      borderTop: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                      paddingInline: "10px",
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    {`${bid?.creditType === "value" ? `₹` : ""} ${
                      bid?.creditValue
                    } ${bid?.creditType === "percentage" ? `%` : ""}`}
                  </td>
                );
              })
            )}
          </tr>

          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                borderRight: "1px solid #ccc",
                height: "40px",
                fontWeight: "bold",
              }}
            >
              Total Base Price:
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => {
                const baseTotal = bid.bidWorkItems.reduce((acc, item) => {
                  const rate = item.rate || 0;
                  const quantity = item.quantity || 0;
                  const conversionRate = item.currency?.rate || 1;

                  const baseTotal = rate * quantity;

                  if (item.currency.currency.shortform !== CURRENCY_INR) {
                    return acc + baseTotal * conversionRate;
                  } else {
                    return acc + baseTotal;
                  }
                }, 0);

                const calculateExtra = (value, type) => {
                  if (!value || !type) return 0;
                  if (type === "percentage") {
                    return baseTotal * (value / 100);
                  }
                  return value;
                };

                const packagingExtra = calculateExtra(
                  bid.packagingValue,
                  bid.packagingType
                );
                const freightExtra = calculateExtra(
                  bid.freightValue,
                  bid.freightType
                );
                const creditExtra = calculateExtra(
                  bid.creditValue,
                  bid.creditType
                );

                const finalTotal =
                  baseTotal + packagingExtra + freightExtra + creditExtra;
                return (
                  <td
                    key={bid.bidId}
                    colSpan={
                      (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                      2 +
                      (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                    }
                    style={{
                      textAlign: "right",
                      fontWeight: "bold",
                      paddingInline: "10px",
                      height: "40px",
                      borderTop: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                    }}
                  >
                    &#8377;{" "}
                    {new Intl.NumberFormat("en-IN", {
                      style: "decimal",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(finalTotal)}
                  </td>
                );
              })
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                height: "40px",
                fontWeight: "bold",
              }}
            >
              GST Amount:
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => {
                const gstTotal = bid.bidWorkItems.reduce((acc, item) => {
                  const rate = item.rate || 0;
                  const quantity = item.quantity || 0;
                  const conversionRate = item.currency?.rate || 1;
                  const gst = item.gst || 0;

                  const baseTotal = rate * quantity;
                  const gstAmount = baseTotal * (gst / 100);

                  if (item.currency.currency.shortform !== CURRENCY_INR) {
                    return acc + gstAmount * conversionRate;
                  } else {
                    return acc + gstAmount;
                  }
                }, 0);
                return (
                  <td
                    key={`${vendor}-grand-${i}`}
                    colSpan={
                      (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                      2 +
                      (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                    }
                    style={{
                      textAlign: "right",
                      paddingInline: "10px",
                      height: "40px",
                      border: "1px solid #ccc",
                      fontWeight: "bold",
                    }}
                  >
                    &#8377;{" "}
                    {new Intl.NumberFormat("en-IN", {
                      style: "decimal",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(gstTotal)}
                  </td>
                );
              })
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                height: "40px",
                fontWeight: "bold",
              }}
            >
              Grand Total including all taxes:
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => {
                const grandTotal = bid.bidWorkItems.reduce((acc, item) => {
                  const rate = item.rate || 0;
                  const quantity = item.quantity || 0;
                  const conversionRate = item.currency?.rate || 1;
                  const gst = item.gst || 0;

                  const baseTotal = rate * quantity;
                  const gstAmount = baseTotal * (gst / 100);

                  if (item.currency.currency.shortform !== CURRENCY_INR) {
                    return acc + (baseTotal + gstAmount) * conversionRate;
                  } else {
                    return acc + baseTotal + gstAmount;
                  }
                }, 0);

                const calculateExtra = (value, type) => {
                  if (!value || !type) return 0;
                  if (type === "percentage") {
                    return grandTotal * (value / 100);
                  }
                  return value;
                };

                const packagingExtra = calculateExtra(
                  bid.packagingValue,
                  bid.packagingType
                );
                const freightExtra = calculateExtra(
                  bid.freightValue,
                  bid.freightType
                );
                const creditExtra = calculateExtra(
                  bid.creditValue,
                  bid.creditType
                );

                const finalTotal =
                  grandTotal + packagingExtra + freightExtra + creditExtra;
                return (
                  <td
                    key={`${vendor}-grand-${i}`}
                    colSpan={
                      (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                      2 +
                      (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                    }
                    style={{
                      textAlign: "right",
                      paddingInline: "10px",
                      height: "40px",
                      border: "1px solid #ccc",
                      fontWeight: "bold",
                    }}
                  >
                    &#8377;{" "}
                    {new Intl.NumberFormat("en-IN", {
                      style: "decimal",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(finalTotal)}
                  </td>
                );
              })
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                height: "40px",
                fontWeight: "bold",
              }}
            >
              Commercial Rating
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => {
                const isLatest = i === bids.length - 1; // Only highlight the latest bid
                const baseTotal = bid.bidWorkItems.reduce((acc, item) => {
                  const rate = item.rate || 0;
                  const quantity = item.quantity || 0;
                  const conversionRate = item.currency?.rate || 1;

                  const subTotal = rate * quantity;
                  return (
                    acc +
                    (item.currency.currency.shortform !== CURRENCY_INR
                      ? subTotal * conversionRate
                      : subTotal)
                  );
                }, 0);

                const calculateExtra = (value, type) => {
                  if (!value || !type) return 0;
                  if (type === "percentage") {
                    return baseTotal * (value / 100);
                  }
                  return value;
                };

                const packagingExtra = calculateExtra(
                  bid.packagingValue,
                  bid.packagingType
                );
                const freightExtra = calculateExtra(
                  bid.freightValue,
                  bid.freightType
                );
                const creditExtra = calculateExtra(
                  bid.creditValue,
                  bid.creditType
                );

                const finalTotal =
                  baseTotal + packagingExtra + freightExtra + creditExtra;

                let bgColor = "white";
                if (isLatest && finalTotal === lowest?.total)
                  bgColor = "lightgreen";
                else if (isLatest && finalTotal === secondLowest?.total)
                  bgColor = "yellow";

                const tag =
                  isLatest && finalTotal === lowest?.total
                    ? "L1"
                    : isLatest && finalTotal === secondLowest?.total
                    ? "L2"
                    : "";

                return (
                  <td
                    key={`${vendor}-rating-${i}`}
                    colSpan={
                      (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                      2 +
                      (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                    }
                    style={{
                      height: "40px",
                      border: "1px solid #ccc",
                      fontWeight: "bold",
                      backgroundColor: bgColor,
                    }}
                  >
                    {tag}
                  </td>
                );
              })
            )}
          </tr>
          <tr>
            <td
              colSpan={50}
              style={{
                borderTop: "1px solid #ccc",
                padding: "8.5px",
                fontWeight: "bold",
                textAlign: "start",
              }}
            >
              Commercial Terms
            </td>
            <td
              colSpan={50}
              style={{ height: "41px", borderRight: "1px solid #ccc" }}
            ></td>
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                padding: "8px",
                fontWeight: "bold",
              }}
            >
              Delivery period (Lead Time)
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => (
                <td
                  key={bid.bidId}
                  colSpan={
                    (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                    2 +
                    (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                  }
                  style={{
                    padding: "10.5px",
                    border: "1px solid #ccc",
                    fontWeight: "bold",
                  }}
                >
                  {bid.deliveryTerms || "N/A"}
                </td>
              ))
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                padding: "8px",
                fontWeight: "bold",
              }}
            >
              Payment Terms
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => (
                <td
                  key={bid.bidId}
                  colSpan={
                    (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                    2 +
                    (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                  }
                  style={{
                    padding: "10.5px",
                    border: "1px solid #ccc",
                    fontWeight: "bold",
                  }}
                >
                  {bid.paymentTerms || "N/A"}
                </td>
              ))
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                padding: "8px",
                fontWeight: "bold",
              }}
            >
              Freight
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => (
                <td
                  key={bid.bidId}
                  colSpan={
                    (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                    2 +
                    (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                  }
                  style={{
                    padding: "10.5px",
                    border: "1px solid #ccc",
                    fontWeight: "bold",
                  }}
                >
                  {bid.freight || "N/A"}
                </td>
              ))
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                padding: "8px",
                fontWeight: "bold",
              }}
            >
              Price Basis (Incoterms)
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => (
                <td
                  key={bid.bidId}
                  colSpan={
                    (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                    2 +
                    (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                  }
                  style={{
                    padding: "10.5px",
                    border: "1px solid #ccc",
                    fontWeight: "bold",
                  }}
                >
                  {bid.priceBasis || "N/A"}
                </td>
              ))
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                padding: "8px",
                fontWeight: "bold",
              }}
            >
              Warranty Period
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => (
                <td
                  key={bid.bidId}
                  colSpan={
                    (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                    2 +
                    (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                  }
                  style={{
                    padding: "10.5px",
                    border: "1px solid #ccc",
                    fontWeight: "bold",
                  }}
                >
                  {bid.warrantyPeriod || "N/A"}
                </td>
              ))
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                padding: "8px",
                fontWeight: "bold",
              }}
            >
              Late Delivery Clause
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => (
                <td
                  key={bid.bidId}
                  colSpan={
                    (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                    2 +
                    (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                  }
                  style={{
                    padding: "10.5px",
                    border: "1px solid #ccc",
                    fontWeight: "bold",
                  }}
                >
                  {bid.deliveryClause || "N/A"}
                </td>
              ))
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                borderRight: "1px solid #ccc",
                padding: "8px",
                fontWeight: "bold",
              }}
            >
              Attachment 1
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => (
                <td
                  key={bid.bidId}
                  colSpan={
                    (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                    2 +
                    (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                  }
                  style={{
                    padding: "9.5px",
                    borderRight: "1px solid #ccc",
                  }}
                >
                  {(bid.attachment1 && bid.attachment1 !== "N/A" && (
                    <Button
                      sx={{
                        margin: "0 !important",
                        padding: "0 !important",
                        width: "max-content !important",
                        fontSize: "12px !important",
                        textDecoration: "underline",
                        textAlign: "start",
                      }}
                      onClick={() => viewPDF(bid.attachment1, storedUser)}
                    >
                      View
                    </Button>
                  )) ||
                    "N/A"}
                </td>
              ))
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                borderRight: "1px solid #ccc",
                padding: "8px",
                fontWeight: "bold",
              }}
            >
              Attachment 2
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => (
                <td
                  key={bid.bidId}
                  colSpan={
                    (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                    2 +
                    (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                  }
                  style={{
                    padding: "9.5px",
                    borderTop: "1px solid #ccc",
                    borderRight: "1px solid #ccc",
                  }}
                >
                  {(bid.attachment2 && bid.attachment2 !== "N/A" && (
                    <Button
                      sx={{
                        margin: "0 !important",
                        padding: "0 !important",
                        width: "max-content !important",
                        fontSize: "12px !important",
                        textDecoration: "underline",
                        textAlign: "start",
                      }}
                      onClick={() => viewPDF(bid.attachment2, storedUser)}
                    >
                      View
                    </Button>
                  )) ||
                    "N/A"}
                </td>
              ))
            )}
          </tr>
          <tr>
            <td
              colSpan={7}
              style={{
                borderTop: "1px solid #ccc",
                borderRight: "1px solid #ccc",
                padding: "8px",
                fontWeight: "bold",
              }}
            >
              Attachment 3
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => (
                <td
                  key={bid.bidId}
                  colSpan={
                    (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                    2 +
                    (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                  }
                  style={{
                    padding: "9.5px",
                    borderTop: "1px solid #ccc",
                    borderRight: "1px solid #ccc",
                  }}
                >
                  {(bid.attachment3 && bid.attachment3 !== "N/A" && (
                    <Button
                      sx={{
                        margin: "0 !important",
                        padding: "0 !important",
                        width: "max-content !important",
                        fontSize: "12px !important",
                        textDecoration: "underline",
                        textAlign: "start",
                      }}
                      onClick={() => viewPDF(bid.attachment3, storedUser)}
                    >
                      View
                    </Button>
                  )) ||
                    "N/A"}
                </td>
              ))
            )}
          </tr>
        </tbody>
      </table>
    </Box>
  );
};

export default ViewAllBidsTableExport;